/**
 * 挂件的主题派生，色值与圆角对照「Shop Agent Chatbox Mockups」。商家只定主色、明暗、圆角、风格、
 * 毛玻璃和字体，其余全部由此算出。主色贴着面板底看不清时按原型换色：对比不足 3:1 沿 oklch
 * 拉开明度，不足 1.5:1 翻转成「主色上的文字色做底、主色做字」。
 */
import type { Settings } from "@go-novus/admin-api";

/** Settings、店面和分镜共用；按已保存的主色识别，不另存一份主题状态。 */
export const CHATBOX_THEME_PRESETS = [
  {
    name: "Rose",
    accent: "#E4649B",
    onPrimary: "#FFFFFF",
    radius: 8,
  },
  {
    name: "Forest",
    accent: "#3F7A5E",
    onPrimary: "#FFFFFF",
    radius: 8,
  },
  {
    name: "Indigo",
    accent: "#5D6EC7",
    onPrimary: "#FFFFFF",
    radius: 8,
  },
  {
    name: "Default",
    accent: "#1C1B1A",
    onPrimary: "#F4F1EA",
    radius: 10,
  },
  {
    name: "Minimal",
    accent: "#FFFFFF",
    onPrimary: "#1C1B1A",
    radius: 20,
  },
  {
    name: "Luxury",
    accent: "#141312",
    onPrimary: "#D9C39A",
    radius: 2,
  },
  {
    name: "Warm",
    accent: "#C4633B",
    onPrimary: "#FBF3EA",
    radius: 12,
  },
  {
    name: "Playful",
    accent: "#5F8C6A",
    onPrimary: "#F1F6EE",
    radius: 999,
  },
] as const satisfies readonly {
  name: string;
  accent: string;
  onPrimary: string;
  radius: Settings["chatbox"]["radius"];
}[];

/** 原型给的浅色中性色。所有主题共用一套，面板、气泡和分隔线都不跟主题色变。 */
const LIGHT_NEUTRALS = {
  panel: "#FFFDFA",
  bubble: "#F5EFE4",
  divider: "#EFE8DB",
  line: "#EAE2D4",
} as const;

export type WidgetDirection = "editorial" | "hairline";
/** 纯展示层接受像素圆角；商家配置的可选值由 admin-api 限定。 */
export type WidgetRadius = number;

/** `light` / `dark` 是预设，也可以直接给店铺的背景色，明暗由亮度判断。 */
export type WidgetSurface = "dark" | "light" | (string & {});

export interface WidgetThemeInput {
  accent?: string;
  /** 显式覆盖预设的主色文字色，不增加商家配置项。 */
  onPrimary?: string;
  blur?: number;
  direction?: WidgetDirection;
  /** 面板字体栈，只看通用族是不是 serif。 */
  font?: string;
  radius?: WidgetRadius;
  surface?: WidgetSurface;
  surfaceOpacity?: number;
}

/**
 * 一条消息渲染所需的全部色值与圆角，面板算好后原样传给 WidgetMessage。
 * 后台 Inbox 回放传进来的一份全是后台的 CSS 变量，所以这里的值都只能原样当 CSS 用，不能拿去算颜色。
 */
export interface WidgetTokens {
  /** 实心主题色底：角色、买家气泡、发送键、加购按钮。低对比时已拉开或翻转。 */
  accentFill: string;
  /** 压在 accentFill 上的字色。 */
  accentFillFg: string;
  /** 主题色淡底：未送达气泡、拾取高亮框。 */
  accentSoft: string;
  /** 主题色的字：复制、查看商品这类链接。 */
  accentText: string;
  /** 请求卡的图标块。主色近乎无彩时淡底只剩一块灰，原型改用实心底。 */
  badgeBg: string;
  badgeFg: string;
  /** AI 气泡的底。 */
  bubbleBg: string;
  bubbleRadius: string;
  cardRadius: string;
  chipRadius: string;
  ctrlRadius: string;
  /** 出错的字色。不跟 accent 走，理由见 deriveTheme。 */
  dangerText: string;
  /** Editorial：AI 回复不套气泡，求助卡使用独立的采集、预览和发送结果组件。 */
  editorial: boolean;
  /** 最弱的灰：消息操作行、页脚。 */
  faint: string;
  ink: string;
  line: string;
  /** Editorial 求助卡操作：主色无彩时用正文色，下划线换成主色上的文字色。 */
  linkText: string;
  linkUnderline: string;
  muted: string;
  /** 衬线字体下强调改用常规字重，辅助小字换无衬线。 */
  serif: boolean;
  /** 复制成功的绿，底色由它按比例混出来。 */
  successText: string;
  subtle: string;
  /** 商品卡的底。 */
  surfaceBg: string;
  /** 卡片托盘的底，也是空态发送键的底。 */
  trayBg: string;
}

/** 后台回放传给 mountReplayMessage 的主题，就是消息用的那一份 token。 */
export type MessageTheme = WidgetTokens;

export interface WidgetTheme extends WidgetTokens {
  /** 商家的原色：店面上的收起入口和主动提示使用，不随面板拉开或翻转。 */
  accent: string;
  /** 压在 accent 上的字色。 */
  accentFg: string;
  /** 浮在店面上的卡片底：主动提示气泡、回到最新。 */
  cardBg: string;
  /** 原色贴着面板底看不清时，给入口角色描的边。 */
  characterEdge: string;
  /** 出错提示的底色。 */
  dangerSoft: string;
  dark: boolean;
  direction: WidgetDirection;
  /** 标题栏下沿、Editorial 轮次之间和输入区上沿的分隔线。 */
  divider: string;
  /** 输入框、建议问题和快捷回答的底。 */
  field: string;
  glass: boolean;
  /** 标题栏图标按钮。 */
  iconColor: string;
  panelBackdrop: string;
  panelBg: string;
  panelRadius: number;
  placeholder: string;
  sendRadius: number;
  /** 生成中扫光的高光色。只在店面面板出现，所以不进消息 token。 */
  shimmerHighlight: string;
  /** 面板不透明时的实底色，也是对比度的参照。 */
  surfaceSolid: string;
}

/** 衬线主题里辅助小字（状态行、地址、复制、页脚）换用的无衬线，取原型的字体栈。 */
export const SERIF_META_FONT = '"Helvetica Neue", Helvetica, Arial, sans-serif';

/** 卡片里的辅助小字：衬线主题换无衬线，其余跟正文走。 */
export function metaFont(t: Pick<WidgetTokens, "serif">): string | undefined {
  return t.serif ? SERIF_META_FONT : undefined;
}

/** 卡片里的强调字重：衬线主题改用常规字重（见 WidgetTokens.serif）。 */
export function strongWeight(t: Pick<WidgetTokens, "serif">): number {
  return t.serif ? 400 : 500;
}

const DEFAULT_ACCENT = "#1C1B1A";
const DEFAULT_RADIUS: WidgetRadius = 8;
const DARK_PANEL = "#181820";
/** 低于这个亮度按暗色底处理。 */
const DARK_SURFACE_LUMINANCE = 0.4;
/** 高于这个亮度的 accent 上面压深色字。 */
const LIGHT_ACCENT_LUMINANCE = 0.42;
/**
 * 扫光高光只是扫过的一道光，不必像 accentText 那样保证正文可读；
 * 但和底色的对比度低于 3:1 时，字被扫到的那段会融进底色消失，才退回 ink。
 */
const SHIMMER_HIGHLIGHT_CONTRAST = 3;
const MIN_SURFACE_OPACITY = 0.4;
/** 原型：主色与面板底对比低于 3:1 拉开明度，低于 1.5:1 翻转。 */
const LIFT_CONTRAST = 3;
const FLIP_CONTRAST = 1.5;
/** 链接文字要到正文对比度。 */
const TEXT_CONTRAST = 4.5;
/** oklch 色度低于它按无彩（黑白灰）处理。 */
const NEUTRAL_CHROMA = 0.03;

function readHexChannels(hex: string): [number, number, number] {
  const raw = hex.replace("#", "");
  const full =
    raw.length === 3
      ? raw
          .split("")
          .map((channel) => channel + channel)
          .join("")
      : raw;
  const value = Number.parseInt(full, 16);

  return [(value >> 16) & 255, (value >> 8) & 255, value & 255];
}

function toLinear(channel: number): number {
  const ratio = channel / 255;
  return ratio <= 0.04045
    ? ratio / 12.92
    : Math.pow((ratio + 0.055) / 1.055, 2.4);
}

function toHex(channels: number[]): string {
  return `#${channels
    .map((channel) =>
      Math.round(Math.min(255, Math.max(0, channel)))
        .toString(16)
        .padStart(2, "0"),
    )
    .join("")
    .toUpperCase()}`;
}

/** WCAG 相对亮度，用来决定压深色字还是浅色字。 */
export function relativeLuminance(hex: string): number {
  const [red, green, blue] = readHexChannels(hex).map(toLinear) as [
    number,
    number,
    number,
  ];
  return 0.2126 * red + 0.7152 * green + 0.0722 * blue;
}

export function contrastRatio(first: string, second: string): number {
  const [light, dark] = [
    relativeLuminance(first),
    relativeLuminance(second),
  ].sort((a, b) => b - a) as [number, number];
  return (light + 0.05) / (dark + 0.05);
}

export function withAlpha(hex: string, alpha: number): string {
  const [red, green, blue] = readHexChannels(hex);
  return `rgba(${String(red)},${String(green)},${String(blue)},${String(alpha)})`;
}

/** 按 sRGB 通道把 `from` 往 `to` 混 `amount`。 */
function mix(from: string, to: string, amount: number): string {
  const target = readHexChannels(to);
  return toHex(
    readHexChannels(from).map(
      (channel, index) => channel + ((target[index] ?? 0) - channel) * amount,
    ),
  );
}

type Oklch = [lightness: number, chroma: number, hue: number];

function toOklch(hex: string): Oklch {
  const [r, g, b] = readHexChannels(hex).map(toLinear) as [
    number,
    number,
    number,
  ];
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  const a = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s;
  const bAxis = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s;
  return [
    0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
    Math.hypot(a, bAxis),
    Math.atan2(bAxis, a),
  ];
}

/** 超出 sRGB 的通道直接截断，色相会有一点偏，换来的是始终能画。 */
function fromOklch([lightness, chroma, hue]: Oklch): string {
  const a = chroma * Math.cos(hue);
  const b = chroma * Math.sin(hue);
  const l = Math.pow(lightness + 0.3963377774 * a + 0.2158037573 * b, 3);
  const m = Math.pow(lightness - 0.1055613458 * a - 0.0638541728 * b, 3);
  const s = Math.pow(lightness - 0.0894841775 * a - 1.291485548 * b, 3);
  return toHex(
    [
      4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
      -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
      -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
    ].map((linear) => {
      const clamped = Math.min(1, Math.max(0, linear));
      const encoded =
        clamped <= 0.0031308
          ? 12.92 * clamped
          : 1.055 * Math.pow(clamped, 1 / 2.4) - 0.055;
      return encoded * 255;
    }),
  );
}

/** 保持色相和色度，沿 oklch 明度往背景的反方向走，直到对比度够为止。 */
function reachContrast(
  color: string,
  background: string,
  target: number,
): string {
  if (contrastRatio(color, background) >= target) return color;
  const [lightness, chroma, hue] = toOklch(color);
  const direction = relativeLuminance(background) < 0.18 ? 1 : -1;
  for (let step = 1; step <= 200; step += 1) {
    const candidate = fromOklch([
      Math.min(1, Math.max(0, lightness + direction * step * 0.005)),
      chroma,
      hue,
    ]);
    if (contrastRatio(candidate, background) >= target) return candidate;
  }
  return direction > 0 ? "#FFFFFF" : "#000000";
}

function isNeutral(hex: string): boolean {
  return toOklch(hex)[1] < NEUTRAL_CHROMA;
}

/** 通用族写在字体栈最后；`sans-serif` 不算。 */
function isSerifStack(font: string): boolean {
  return /(^|,)\s*serif\s*$/i.test(font);
}

export function deriveTheme(input: WidgetThemeInput = {}): WidgetTheme {
  const accent = (input.accent ?? DEFAULT_ACCENT).toUpperCase();
  const preset = CHATBOX_THEME_PRESETS.find(
    (candidate) => candidate.accent === accent,
  );
  const surface = input.surface ?? "light";
  const surfaceIsHex = surface.startsWith("#");
  const dark =
    surface === "dark" ||
    (surfaceIsHex && relativeLuminance(surface) < DARK_SURFACE_LUMINANCE);

  const accentIsLight = relativeLuminance(accent) > LIGHT_ACCENT_LUMINANCE;
  const accentFg =
    input.onPrimary ??
    preset?.onPrimary ??
    (accentIsLight ? "#111111" : "#ffffff");

  const baseSurface = surfaceIsHex
    ? surface
    : dark
      ? DARK_PANEL
      : LIGHT_NEUTRALS.panel;
  const accentContrast = contrastRatio(accent, baseSurface);
  const flipped = accentContrast < FLIP_CONTRAST;
  // 原型 5c：近黑主色在深色面板上翻转后，面板直接用主色做底，整块才是黑底金字；
  // 毛玻璃也比 4c 实一档（.78 对 .74），金字才压得住底下的店面。
  const accentPanel = dark && flipped && !surfaceIsHex && isNeutral(accent);
  const surfaceSolid = accentPanel ? accent : baseSurface;
  const accentFill = flipped
    ? accentFg
    : accentContrast < LIFT_CONTRAST
      ? reachContrast(accent, surfaceSolid, LIFT_CONTRAST)
      : accent;
  const accentFillFg = flipped ? accent : accentFg;
  const fillIsNeutral = isNeutral(accentFill);

  const requestedOpacity = Math.max(
    MIN_SURFACE_OPACITY,
    input.surfaceOpacity ?? 1,
  );
  const opacity = Math.min(
    1,
    requestedOpacity + (accentPanel && requestedOpacity < 1 ? 0.04 : 0),
  );
  const blur = input.blur ?? 0;
  const glass = opacity < 1;

  const ink = dark ? "#F4F1EA" : "#1C1B1A";
  // 原型 5c：翻转后分隔线也带一点主色上的文字色。
  const darkLineTint = flipped ? accentFill : "#FFFFFF";
  // 浅色毛玻璃会透出店面颜色，暖米色的线和气泡压在彩色店面上发脏：线换成半透明中性色，气泡换白底。
  // 线不能去掉：店面是白色时面板也近乎纯白，白底的卡片和输入框全靠这条线分出边界。
  const line = dark
    ? withAlpha(darkLineTint, flipped ? 0.22 : 0.12)
    : glass
      ? withAlpha(ink, 0.08)
      : LIGHT_NEUTRALS.line;
  const divider = dark
    ? withAlpha(darkLineTint, flipped ? 0.18 : 0.1)
    : glass
      ? withAlpha(ink, 0.06)
      : LIGHT_NEUTRALS.divider;
  // 气泡照旧不描边，代价是白色店面上白气泡和面板几乎分不开；这是定过的取舍，别再加描边。
  const bubbleBg = dark
    ? withAlpha("#FFFFFF", 0.08)
    : glass
      ? "#FFFFFF"
      : LIGHT_NEUTRALS.bubble;
  // 灰字和图标同理：原型的暖灰压在透出来的店面颜色上又脏又淡，店面一深就读不出来。
  // 换成半透明正文色，跟着底色一起变深，次要字保持 4.5:1 以上、图标 3:1 以上；别换回实色灰。
  const glassGray = (alpha: number, solid: string) =>
    glass ? withAlpha(ink, alpha) : solid;
  // 错误色不跟着 accent 走：商家把 accent 设成红或绿时，出错就分辨不出来了。
  const dangerText = dark ? "#FDA29B" : "#B42318";

  const accentText = dark
    ? contrastRatio(accentFill, surfaceSolid) >= TEXT_CONTRAST
      ? accentFill
      : mix(accentFill, "#FFFFFF", 0.55)
    : reachContrast(
        // 原型 5a：黑色主色的链接用主色上的金色压暗，不用黑。
        fillIsNeutral ? accentFillFg : accentFill,
        surfaceSolid,
        TEXT_CONTRAST,
      );

  const radius = Math.max(0, input.radius ?? DEFAULT_RADIUS);
  // 原型三档：圆角 16 / 8 / 2 → 面板 20 / 12 / 4、气泡 16 / 10 / 4、卡片 14 / – / 4、发送键 11 / 8 / 2。
  const panelRadius =
    radius === 0 ? 0 : Math.min(Math.max(radius * 1.5, radius + 2), 20);
  const bubbleRadius = radius === 0 ? 0 : Math.min(radius + 2, 16);
  const cardRadius = radius === 0 ? 0 : Math.min(Math.max(radius, 4), 14);
  const direction = input.direction ?? "hairline";

  return {
    accent,
    accentFg,
    accentFill,
    accentFillFg,
    accentSoft: withAlpha(accentFill, dark ? 0.22 : 0.1),
    accentText,
    badgeBg: fillIsNeutral
      ? accentFill
      : dark
        ? withAlpha(accentFill, 0.25)
        : mix("#FFFFFF", accentFill, 0.08),
    badgeFg: fillIsNeutral ? accentFillFg : accentText,
    bubbleBg,
    bubbleRadius: `${String(bubbleRadius)}px`,
    cardBg: dark
      ? glass
        ? withAlpha(surfaceSolid, Math.min(1, opacity + 0.15))
        : surfaceSolid
      : "#FFFFFF",
    cardRadius: `${String(cardRadius)}px`,
    characterEdge:
      contrastRatio(accent, surfaceSolid) < FLIP_CONTRAST
        ? line
        : "transparent",
    chipRadius: radius === 0 ? "0px" : "999px",
    ctrlRadius: `${String(bubbleRadius)}px`,
    dangerSoft: withAlpha(dangerText, dark ? 0.14 : 0.08),
    dangerText,
    dark,
    direction,
    divider,
    editorial: direction === "editorial",
    faint: dark ? withAlpha(ink, 0.45) : glassGray(0.5, "#A8A196"),
    field: dark ? withAlpha("#FFFFFF", 0.06) : "#FFFFFF",
    glass,
    iconColor: dark ? withAlpha(ink, 0.7) : glassGray(0.7, "#67625A"),
    ink,
    line,
    linkText: fillIsNeutral ? ink : accentText,
    linkUnderline: fillIsNeutral ? accentFillFg : withAlpha(accentText, 0.35),
    muted: dark ? withAlpha(ink, 0.55) : glassGray(0.64, "#8A847A"),
    panelBackdrop: blur > 0 ? `blur(${String(blur)}px)` : "none",
    panelBg: glass ? withAlpha(surfaceSolid, opacity) : surfaceSolid,
    panelRadius,
    placeholder: dark ? withAlpha(ink, 0.45) : glassGray(0.6, "#9A948A"),
    sendRadius: Math.min(radius, 11),
    // 跟面板上实际画出来的主题色走（已拉开或翻转），深色面板上才是提亮后的主题色。
    shimmerHighlight:
      contrastRatio(accentFill, surfaceSolid) < SHIMMER_HIGHLIGHT_CONTRAST
        ? ink
        : accentFill,
    serif: isSerifStack(input.font ?? ""),
    subtle: dark ? withAlpha(ink, 0.09) : "#EFECE6",
    successText: dark ? "#7FD9A2" : "#1E8E4E",
    surfaceBg: dark ? bubbleBg : "#FFFFFF",
    surfaceSolid,
    // 浅色实底面板上托盘和 AI 气泡同一块暖米色：原来的半透明墨色叠在暖白面板上偏冷灰，
    // 紧挨着米色气泡像两套颜色。毛玻璃上气泡是白底，托盘要比白色纸面深一档；深色面板上亮一档。
    trayBg: dark
      ? withAlpha(ink, 0.08)
      : glass
        ? withAlpha(ink, 0.04)
        : LIGHT_NEUTRALS.bubble,
  };
}
